import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { lazy } from 'react';
import { PERMISSIONS, holdsPermission } from '@/api/permissions';
import { paths } from '@/routes/paths';
import { RequirePermission } from '@/routes/guards';
import type { ModuleManifest } from './manifest';

const OrganizationTypesPage = lazy(() => import('@/features/organizationTypes/OrganizationTypesPage'));
const SpecialtiesPage = lazy(() => import('@/features/specialties/SpecialtiesPage'));
const GovernoratesPage = lazy(() => import('@/features/governorates/GovernoratesPage'));
const AuditLogPage = lazy(() => import('@/features/audit/AuditLogPage'));
const RegisterImportsPage = lazy(() => import('@/features/registryImports/RegisterImportsPage'));

/**
 * Reference data — the twice-a-year errand: the lists every tenant picks from,
 * plus the audit trail, which is the same kind of work (platform staff looking
 * something up) and does not deserve a tile of its own.
 */
export const referenceModule: ModuleManifest = {
  key: 'reference',
  titleKey: 'modules.reference.title',
  descriptionKey: 'modules.reference.description',
  icon: SettingsRoundedIcon,
  prefix: '/reference',
  audience: 'platform',
  anyOf: [PERMISSIONS.REFERENCE_WRITE, PERMISSIONS.AUDIT_READ],
  sections: [
    {
      key: 'reference',
      items: [
        {
          key: 'organizationTypes',
          labelKey: 'nav.organizationTypes',
          to: paths.organizationTypes,
          icon: <CategoryRoundedIcon />,
          anyOf: [PERMISSIONS.REFERENCE_WRITE],
        },
        {
          key: 'specialties',
          labelKey: 'nav.specialties',
          to: paths.specialties,
          icon: <LocalHospitalRoundedIcon />,
          anyOf: [PERMISSIONS.REFERENCE_WRITE],
        },
        {
          // Read-only: the list is seeded by migration and has no write endpoint.
          key: 'governorates',
          labelKey: 'governorate.nav',
          to: paths.governorates,
          icon: <MapOutlinedIcon />,
          anyOf: [PERMISSIONS.REFERENCE_WRITE],
        },
        {
          /*
           * The two external register mirrors — the syndicate's registers
           * behind the registration form's name autocomplete and the Gudea
           * medicines register behind the prescribing typeahead. Reference
           * data, and the same twice-a-year errand: nothing else on the
           * platform says the data behind those two features is stale.
           */
          key: 'registerImports',
          labelKey: 'nav.registerImports',
          to: paths.registerImports,
          icon: <CloudSyncRoundedIcon />,
          anyOf: [PERMISSIONS.REFERENCE_WRITE],
        },
        {
          key: 'auditLog',
          labelKey: 'nav.auditLog',
          to: paths.auditLog,
          icon: <HistoryRoundedIcon />,
          anyOf: [PERMISSIONS.AUDIT_READ],
        },
      ],
    },
  ],
  routes: [
    {
      element: <RequirePermission permission={[PERMISSIONS.REFERENCE_WRITE]} />,
      children: [
        { path: paths.organizationTypes, element: <OrganizationTypesPage /> },
        { path: paths.specialties, element: <SpecialtiesPage /> },
        { path: paths.governorates, element: <GovernoratesPage /> },
        { path: paths.registerImports, element: <RegisterImportsPage /> },
      ],
    },
    {
      element: <RequirePermission permission={[PERMISSIONS.AUDIT_READ]} />,
      children: [{ path: paths.auditLog, element: <AuditLogPage /> }],
    },
  ],
  landing: (viewer) =>
    holdsPermission(viewer.permissions, PERMISSIONS.REFERENCE_WRITE)
      ? paths.organizationTypes
      : paths.auditLog,
};
